import { Helmet } from 'react-helmet';
import { generateImageStructuredData, generateImageTitle, generateAltText, extractFilename } from '@/utils/imageSEO';

interface ImageWithSEOProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  title?: string;
  context?: string; // Page or section context
  keywords?: string[];
  width?: number;
  height?: number;
  includeStructuredData?: boolean;
  includeOGTags?: boolean;
}

/**
 * Image component with automatic SEO metadata generation
 */
const ImageWithSEO = ({
  src,
  alt,
  title,
  context,
  keywords,
  width,
  height,
  includeStructuredData = false,
  includeOGTags = false,
  className,
  ...props
}: ImageWithSEOProps) => {
  const filename = extractFilename(src);
  
  // Auto-generate title and alt if not provided
  const imageTitle = title || generateImageTitle(filename, context);
  const imageAlt = alt || generateAltText(filename, context, keywords);
  
  const siteUrl = 'https://capitaldistrictsocial.com';
  const fullImageUrl = src.startsWith('http') ? src : `${siteUrl}${src}`;

  const structuredData = includeStructuredData 
    ? generateImageStructuredData({ src, alt: imageAlt, title: imageTitle, width, height, context })
    : null;

  return (
    <>
      {(includeStructuredData || includeOGTags) && (
        <Helmet>
          {includeStructuredData && structuredData && (
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>
          )}
          
          {includeOGTags && (
            <>
              <meta property="og:image" content={fullImageUrl} />
              <meta property="og:image:alt" content={imageAlt} />
              {width && <meta property="og:image:width" content={width.toString()} />}
              {height && <meta property="og:image:height" content={height.toString()} />}
              
              <meta name="twitter:image" content={fullImageUrl} />
              <meta name="twitter:image:alt" content={imageAlt} />
            </>
          )}
        </Helmet>
      )}
      
      <img
        src={src}
        alt={imageAlt}
        title={imageTitle}
        width={width}
        height={height}
        className={className}
        {...props}
      />
    </>
  );
};

export default ImageWithSEO;